import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PhoneInput from 'react-phone-number-input';
import { isSupportedCountry } from 'libphonenumber-js';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ClipboardJS from 'clipboard';

import { HelpOutline, FileCopy } from '@material-ui/icons';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import Checkbox from '@material-ui/core/Checkbox';
import Tooltip from '@material-ui/core/Tooltip';
import InputAdornment from '@material-ui/core/InputAdornment';
import SwitchField from '@material-ui/core/Switch';
import FormLabel from '@material-ui/core/FormLabel';
import IconButton from '@material-ui/core/IconButton';
import { reverseObject } from 'utils/system';

import withStyles from './styles';
import './phone.css';
import './recurly.css';

const getServerSideErrorMessage = error => {
    if (typeof error === 'string') {
        return error;
    } else {
        return error && error.serverSide && error.message;
    }
};

const OneLine = ({ classes, children }) => (
    <div className={classes.oneLine}>
        {React.Children.map(children, child => (
            <div className={classes.oneLineChild}>{child}</div>
        ))}
    </div>
);

const Text = ({
    classes,
    value,
    error,
    required,
    disabled,
    label,
    placeholder,
    helper,
    onChange,
    inputProps,
    type,
    half,
    oneLine,
    autoFocus,
    onEnterPress,
    className,
}) => (
    <TextField
        type={type}
        style={{ width: half && '50%', marginRight: oneLine && '50%' }}
        error={!!error}
        value={value}
        required={required}
        disabled={disabled}
        autoFocus={autoFocus}
        placeholder={placeholder}
        label={label}
        onChange={event => onChange(event.target.value, event)}
        className={classNames(classes.text, className)}
        margin="normal"
        variant="outlined"
        fullWidth
        inputProps={inputProps}
        helperText={helper || getServerSideErrorMessage(error)}
        onKeyPress={ev => {
            if (ev.key === 'Enter' && onEnterPress) {
                onEnterPress();
                ev.preventDefault();
            }
        }}
    />
);

Text.propTypes = {
    label: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
};

class CopyTextField extends React.Component {
    state = {
        labelWidth: 0,
    };
    constructor(props) {
        super(props);

        this.inputRef = React.createRef();
        this.buttonRef = React.createRef();
        this.labelRef = React.createRef();
    }
    componentDidUpdate() {
        if (!this.buttonRef.current) {
            return;
        }
        this.clipboard = new ClipboardJS(this.buttonRef.current, {
            target: () => this.inputRef.current,
        });
    }
    componentWillUnmount() {
        if (this.clipboard) {
            this.clipboard.destroy();
        }
    }
    componentDidMount() {
        const label = ReactDOM.findDOMNode(this.labelRef.current);
        this.setState({
            labelWidth: label.offsetWidth,
        });
    }
    render() {
        const {
            value,
            error,
            required,
            disabled,
            label,
            onChange,
            classes,
        } = this.props;
        const { labelWidth } = this.state;
        return (
            <FormControl
                margin="normal"
                variant="outlined"
                error={!!error}
                required={required}
                disabled={disabled}
                label={label}
                fullWidth
            >
                <InputLabel ref={this.labelRef}>{label}</InputLabel>
                <OutlinedInput
                    inputRef={this.inputRef}
                    labelWidth={labelWidth}
                    value={value}
                    onChange={event => onChange(event.target.value, event)}
                    endAdornment={
                        <InputAdornment position="end">
                            <IconButton ref={this.buttonRef}>
                                <FileCopy />
                            </IconButton>
                        </InputAdornment>
                    }
                    className={classes.copyTextFieldInput}
                />
            </FormControl>
        );
    }
}

class SelectComponent extends React.Component {
    constructor(props) {
        super(props);
        this.labelRef = React.createRef();
        this.state = {
            labelWidth: 0,
        };
    }
    componentDidMount() {
        const label = ReactDOM.findDOMNode(this.labelRef.current);
        this.setState({
            labelWidth: label.offsetWidth,
        });
    }
    render() {
        const {
            label,
            value,
            error,
            disabled,
            onChange,
            classes,
            native,
            inputProps,
            half,
            oneLine,
            sortOptions,
            open,
        } = this.props;
        let { options } = this.props;
        options = reverseObject(options || {});
        const { labelWidth } = this.state;
        const errorMessage = getServerSideErrorMessage(error);
        const Option = native ? 'option' : MenuItem;
        const optionsNames = Object.keys(options);

        if (sortOptions) {
            optionsNames.sort((a, b) => {
                if (a.toLowerCase() < b.toLowerCase()) return -1;
                if (a.toLowerCase() > b.toLowerCase()) return 1;
                return 0;
            });
        }

        return (
            <FormControl
                className={classes.selectContainer}
                margin="normal"
                variant="outlined"
                error={!!error}
                style={{ width: half && '50%', marginRight: oneLine && '50%' }}
            >
                <InputLabel ref={this.labelRef}>{label}</InputLabel>
                <Select
                    value={value}
                    error={!!error}
                    disabled={disabled}
                    onChange={event => onChange(event.target.value, event)}
                    input={<OutlinedInput labelWidth={labelWidth} />}
                    inputProps={inputProps}
                    native={native}
                    open={open}
                >
                    {optionsNames.map(name => (
                        <Option key={options[name]} value={options[name]}>
                            {name}
                        </Option>
                    ))}
                </Select>
                {errorMessage && (
                    <FormHelperText>{errorMessage}</FormHelperText>
                )}
            </FormControl>
        );
    }
}

const Phone = ({
    classes,
    label,
    disabled,
    value,
    error,
    country,
    onChange,
    valueFromProps,
    helper,
    className,
}) => {
    const [isFocus, setFocus] = useState(false);
    const [phone, setPhone] = useState(value);

    useEffect(() => {
        if (valueFromProps) {
            setPhone(value);
        }
    }, [value]);

    const onPhoneChange = val => {
        if (!valueFromProps) {
            setPhone(val);
        }
        onChange(val);
    };
    const focused = () => isFocus || !!phone;

    const containerClassName = classNames(classes.phoneContainer, className, {
        [classes.phoneContainerFocus]: focused(),
    });
    const labelClassName = classNames(classes.phoneLabel, {
        [classes.phoneLabelFocus]: focused(),
    });
    const supportedCountry = isSupportedCountry(country) ? country : 'US';
    return (
        <>
            <FormControl
                className={containerClassName}
                margin="normal"
                variant="outlined"
            >
                <InputLabel className={labelClassName}>{label}</InputLabel>
                <PhoneInput
                    error={error}
                    disabled={disabled}
                    country={supportedCountry}
                    onFocus={() => setFocus(true)}
                    onBlur={() => setFocus(false)}
                    className={classes.phoneNumber}
                    value={phone}
                    onChange={onPhoneChange}
                />
            </FormControl>
            {helper && (
                <FormHelperText style={{ marginTop: 0 }} variant="outlined">
                    {helper}
                </FormHelperText>
            )}
        </>
    );
};

class Recurly extends React.Component {
    constructor(props) {
        super(props);
        this.labelRef = React.createRef();
        this.state = {
            labelWidth: 0,
        };
    }
    componentDidMount() {
        const label = ReactDOM.findDOMNode(this.labelRef.current);
        this.setState({
            labelWidth: label.offsetWidth,
        });
    }
    render() {
        const { label, error, classes, recurlyName } = this.props;
        const { labelWidth } = this.state;

        return (
            <FormControl
                className={classes.recurlyContainer}
                margin="normal"
                variant="outlined"
                error={!!error}
            >
                <InputLabel
                    className={classes.recurlyLabel}
                    ref={this.labelRef}
                >
                    {label}
                </InputLabel>
                <OutlinedInput
                    labelWidth={labelWidth}
                    inputComponent="div"
                    inputProps={{ 'data-recurly': recurlyName }}
                />
            </FormControl>
        );
    }
}

const CheckBox = ({
    classes,
    label,
    help,
    error,
    checked,
    disabled,
    readonly,
    onChange,
}) => {
    const labelInfo = (
        <React.Fragment>
            <span className={classNames({ [classes.checkBoxError]: error })}>
                {label}
            </span>
            {help && (
                <Tooltip title={help}>
                    <HelpOutline className={classes.checkBoxHelpIcon} />
                </Tooltip>
            )}
        </React.Fragment>
    );
    const changeHandler = event => !readonly && onChange(event.target.checked);
    return (
        <FormControlLabel
            className={classes.checkBoxContainer}
            control={
                <Checkbox
                    checked={checked}
                    onChange={changeHandler}
                    disabled={disabled}
                />
            }
            label={labelInfo}
        />
    );
};

const Switch = ({ value, label, legend = '', disabled, onChange }) => {
    const changeHandler = event => onChange(event.target.checked);
    const control = (
        <SwitchField
            disabled={disabled}
            checked={value}
            onChange={changeHandler}
        />
    );
    return (
        <FormControl component="fieldset">
            {legend && <FormLabel component="legend">{legend}</FormLabel>}
            <FormControlLabel control={control} label={label} />
        </FormControl>
    );
};

const Row = ({ classes, children }) => (
    <div className={classes.row}>{children}</div>
);

export default {
    Text: withStyles(Text),
    Select: withStyles(SelectComponent),
    Phone: withStyles(Phone),
    OneLine: withStyles(OneLine),
    CheckBox: withStyles(CheckBox),
    Recurly: withStyles(Recurly),
    CopyTextField: withStyles(CopyTextField),
    Row: withStyles(Row),
    Switch: withStyles(Switch),
};
